/**
 * deviceId.ts
 *
 * A random identifier for this browser install, so support can tell two
 * devices of the same model apart. It is not tied to the account and is kept
 * across logout.
 */

import { newUuid } from "@/utils/uuid";

/** localStorage key holding the id. */
export const DEVICE_ID_STORAGE_KEY = "deviceId";

/** Shape of an acceptable stored id; anything else is regenerated. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Used when localStorage is unavailable: stable for the tab's lifetime. */
let memoryId: string | null = null;

/**
 * Returns this browser's device id, creating and storing one on first use.
 *
 * @returns A lowercase v4 UUID.
 */
export function getOrCreateDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (stored && UUID_SHAPE.test(stored)) return stored;
    const id = newUuid();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    return id;
  } catch {
    memoryId ??= newUuid();
    return memoryId;
  }
}
